import React, { useState } from "react";
import {
  Building,
  CheckCircle,
  ArrowRight,
  Briefcase,
  Landmark,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import ConsultationForm from "@/components/forms/ConsultationForm";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import WhatsAppWidget from "@/components/chat/WhatsAppWidget";

const BusinessSetupPage = () => {
  const [showConsultationForm, setShowConsultationForm] = useState(false);

  const openConsultationForm = () => {
    setShowConsultationForm(true);
  };

  return (
    <div className="bg-white min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900">
              Business Formation & Banking Solutions in the UAE
            </h1>
            <p className="text-xl text-slate-700 mb-8">
              Open a UAE bank account and establish your business with expert
              guidance. Get your company set up in as little as 3-5 days.
            </p>
            <Button
              onClick={openConsultationForm}
              size="lg"
              className="bg-coral-600 hover:bg-coral-700 text-white px-8 py-6 text-lg"
            >
              Get a Free Consultation <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-slate-900">
              Why Choose Cordi for Business Setup & Banking
            </h2>
            <p className="text-lg text-slate-700">
              We partner with banks and financial providers to offer diverse
              options. Each application is tailored to meet unique business
              needs, whether you're a UAE citizen or not.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="bg-coral-100 p-3 rounded-full w-14 h-14 flex items-center justify-center mb-4">
                <FileText className="h-7 w-7 text-coral-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Easy Application</h3>
              <p className="text-slate-600">
                Online UAE business setup and bank account opening is super easy
                with just a simple form.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="bg-coral-100 p-3 rounded-full w-14 h-14 flex items-center justify-center mb-4">
                <Landmark className="h-7 w-7 text-coral-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">
                Trusted Bank Partners
              </h3>
              <p className="text-slate-600">
                Make an informed choice from a range of reputable banks and
                financial services.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="bg-coral-100 p-3 rounded-full w-14 h-14 flex items-center justify-center mb-4">
                <CheckCircle className="h-7 w-7 text-coral-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Guided Support</h3>
              <p className="text-slate-600">
                Get personalized assistance for a successful UAE business setup
                and banking process.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="bg-coral-100 p-3 rounded-full w-14 h-14 flex items-center justify-center mb-4">
                <Briefcase className="h-7 w-7 text-coral-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">
                Ensuring Compliance
              </h3>
              <p className="text-slate-600">
                A business bank account is crucial for accurate financial
                records and tax filings.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={openConsultationForm}
              variant="outline"
              size="lg"
              className="border-coral-600 text-coral-600 hover:bg-coral-50"
            >
              Get Expert Advice <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Process Explained Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-slate-900">
              The Process Explained
            </h2>
            <p className="text-lg text-slate-700 mb-8">
              After obtaining your EIN, we will guide you through a series of
              essential questions to help us better understand your business.
              Once your application is reviewed by the bank within 3-5 days, you
              can expect to receive a physical debit card if approved.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="relative">
              <div className="bg-coral-100 rounded-full p-6 w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <Landmark className="h-8 w-8 text-coral-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-center">Step 1</h3>
              <h4 className="text-lg font-medium mb-2 text-center">
                Select a bank
              </h4>
              <p className="text-slate-600 text-center">
                Select from a range of reputable banking partners that match
                your specific preferences.
              </p>
            </div>

            <div className="relative">
              <div className="bg-coral-100 rounded-full p-6 w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <FileText className="h-8 w-8 text-coral-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-center">Step 2</h3>
              <h4 className="text-lg font-medium mb-2 text-center">
                Fill out the form
              </h4>
              <p className="text-slate-600 text-center">
                Submit essential information and your account preferences
                through our online form.
              </p>
            </div>

            <div className="relative">
              <div className="bg-coral-100 rounded-full p-6 w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-coral-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-center">Step 3</h3>
              <h4 className="text-lg font-medium mb-2 text-center">
                Await approval
              </h4>
              <p className="text-slate-600 text-center">
                Once we receive your EIN, our agents will submit your
                application within one day.
              </p>
            </div>

            <div className="relative">
              <div className="bg-coral-100 rounded-full p-6 w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <Building className="h-8 w-8 text-coral-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-center">Step 4</h3>
              <h4 className="text-lg font-medium mb-2 text-center">
                Account activation
              </h4>
              <p className="text-slate-600 text-center">
                Once approved, bank account will become active and you'll be
                able to receive payments.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={openConsultationForm}
              size="lg"
              className="bg-coral-600 hover:bg-coral-700 text-white px-8"
            >
              Start Your Application <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />

      {/* WhatsApp Widget */}
      <WhatsAppWidget />

      {/* Consultation Form */}
      <ConsultationForm
        open={showConsultationForm}
        onOpenChange={setShowConsultationForm}
      />
    </div>
  );
};

export default BusinessSetupPage;
